import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { cn } from '../../utils/cn';


const AutocompleteDropdown = React.forwardRef(({
  className,
  options = [],
  value,
  placeholder = 'Select or type...',
  disabled = false,
  required = false,
  label,
  description,
  error,
  clearable = true,
  loading = false,
  id,
  name,
  onChange,
  onOpenChange,
  ...props
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selectId = id || `autocomplete-${Math.random()?.toString(36)?.substr(2, 9)}`;

  // Update input value when prop value changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Filter options based on input
  useEffect(() => {
    if (!inputValue) {
      setFilteredOptions(options);
    } else {
      const filtered = options?.filter(option =>
        option?.label?.toLowerCase()?.includes(inputValue?.toLowerCase()) ||
        option?.value?.toLowerCase()?.includes(inputValue?.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [inputValue, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef?.current && !containerRef?.current?.contains(event?.target)) {
        setIsOpen(false);
        onOpenChange?.(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onOpenChange]);

  const handleInputChange = (e) => {
    const newValue = e?.target?.value;
    setInputValue(newValue);
    onChange?.(newValue);
    
    if (!isOpen) {
      setIsOpen(true);
      onOpenChange?.(true);
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    onOpenChange?.(true);
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter') {
      e?.preventDefault();
      // Accept the current input value as custom value
      onChange?.(inputValue);
      setIsOpen(false);
      onOpenChange?.(false);
      inputRef?.current?.blur();
    }
  };

  const handleOptionSelect = (option) => {
    setInputValue(option?.value);
    onChange?.(option?.value);
    setIsOpen(false);
    onOpenChange?.(false);
    inputRef?.current?.blur();
  };

  const handleClear = (e) => {
    e?.stopPropagation();
    setInputValue('');
    onChange?.('');
    inputRef?.current?.focus();
  };

  const handleToggleDropdown = () => {
    if (!disabled) {
      const newIsOpen = !isOpen;
      setIsOpen(newIsOpen);
      onOpenChange?.(newIsOpen);
      if (newIsOpen) {
        inputRef?.current?.focus();
      }
    }
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {label && (
        <label
          htmlFor={selectId}
          className={cn(
            'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 mb-2 block',
            error ? 'text-destructive' : 'text-foreground'
          )}
        >
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        <div className="relative">
          <input
            ref={(node) => {
              inputRef.current = node;
              if (typeof ref === 'function') {
                ref(node);
              } else if (ref) {
                ref.current = node;
              }
            }}
            id={selectId}
            name={name}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            className={cn(
              'flex h-10 w-full rounded-md border border-gray-300 bg-white text-black px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pr-20',
              error && 'border-destructive focus:ring-destructive'
            )}
            {...props}
          />

          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {loading && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}

            {clearable && inputValue && !loading && (
              <button
                type="button"
                onClick={handleClear}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}

            <button
              type="button"
              onClick={handleToggleDropdown}
              disabled={disabled}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronDown className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
            </button>
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white text-black border border-border rounded-md shadow-md max-h-60 overflow-auto">
            {filteredOptions?.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                {inputValue ? 'No matching options. Press Enter to use custom value.' : 'No options available'}
              </div>
            ) : (
              <div className="py-1">
                {filteredOptions?.map((option, index) => (
                  <div
                    key={`${option?.value}-${index}`}
                    className={cn(
                      'relative flex cursor-pointer select-none items-center rounded-sm px-3 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                      inputValue === option?.value && 'bg-primary text-primary-foreground'
                    )}
                    onClick={() => handleOptionSelect(option)}
                  >
                    <span className="flex-1">{option?.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {description && !error && (
        <p className="text-sm text-muted-foreground mt-1">
          {description}
        </p>
      )}
      {error && (
        <p className="text-sm text-destructive mt-1">
          {error}
        </p>
      )}
    </div>
  );
});

AutocompleteDropdown.displayName = 'AutocompleteDropdown';

export default AutocompleteDropdown;